import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtDate, num } from "../lib/format";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Skeleton } from "./ui/skeleton";
import { User, Cpu, UserCog, HelpCircle, ArrowRight, Lock, History, Sparkles, Link2, Check } from "lucide-react";

function actorIcon(type) {
  if (type === "PRACTITIONER") return <UserCog size={14} className="text-muted-foreground" />;
  if (type === "SYSTEM") return <Cpu size={14} className="text-muted-foreground" />;
  if (type === "EMPLOYEE") return <User size={14} className="text-muted-foreground" />;
  return <HelpCircle size={14} className="text-muted-foreground" />;
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function EventDialog({ eventId, open, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["audit-event", eventId],
    queryFn: async () => (await api.get(`/audit/events/${eventId}`)).data,
    enabled: !!eventId && open,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Change Detail</DialogTitle>
          <DialogDescription>Field-level before and after values for this audit event.</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <Skeleton className="h-40" />
        ) : (
          <div>
            <div className="grid grid-cols-2 gap-4 rounded-md border border-border bg-muted/40 p-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Actor</div>
                <div className="mt-0.5 flex items-center gap-1.5 font-medium">
                  {actorIcon(data.event.actor_type)} {data.event.actor_name_as_printed || "—"}
                </div>
                {data.event.actor_login && (
                  <div className="font-mono text-xs text-muted-foreground">{data.event.actor_login}</div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Subject</div>
                <div className="mt-0.5 font-medium">{data.event.subject_name_as_printed || "—"}</div>
                <div className="font-mono text-xs text-muted-foreground">{data.event.subject_resolution}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">When</div>
                <div className="mt-0.5 font-mono">{fmtTime(data.event.event_timestamp_utc)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Type</div>
                <div className="mt-0.5">{data.event.event_type || "Update"}</div>
              </div>
            </div>

            <div className="mt-4 rounded-md border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Field</TableHead>
                    <TableHead>Old Value</TableHead>
                    <TableHead></TableHead>
                    <TableHead>New Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.changes.map((c, i) => (
                    <TableRow key={`${c.native_field_name}-${i}`} className={c.value_changed ? "" : "opacity-60"}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-1.5">
                          {c.is_sensitive && <Lock size={12} className="text-warn" />}
                          {c.native_field_name}
                        </span>
                        {c.canonical_field_name && (
                          <span className="font-mono text-xs text-muted-foreground">{c.canonical_field_name}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground max-w-[180px] truncate">
                        {c.old_value ?? (c.old_value_numeric != null ? num(c.old_value_numeric, 2) : "—")}
                      </TableCell>
                      <TableCell><ArrowRight size={14} className="text-muted-foreground" /></TableCell>
                      <TableCell className="font-mono text-xs font-semibold max-w-[180px] truncate">
                        {c.new_value ?? (c.new_value_numeric != null ? num(c.new_value_numeric, 2) : "—")}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.changes.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-muted-foreground">No field changes recorded.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AuditEvents({ clientId, employeeId, limit = 150, dateFrom, dateTo, windowFrom, windowTo, matchGuids = [], matchReasons = {}, primaryGuid, actor, eventType, onLink, linkedGuid, linkPending, emptyTitle, emptyText = "No audit events recorded." }) {
  const [selected, setSelected] = useState(null);
  const matchSet = new Set(matchGuids);
  const params = new URLSearchParams();
  if (employeeId) params.set("employee", employeeId);
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  if (actor) params.set("actor", actor);
  if (eventType) params.set("event_type", eventType);
  params.set("limit", String(limit));

  const { data, isLoading, error } = useQuery({
    queryKey: ["audit", clientId, employeeId, limit, dateFrom, dateTo, actor, eventType],
    queryFn: async () => (await api.get(`/clients/${clientId}/audit?${params.toString()}`)).data,
    enabled: !!clientId,
  });

  const inWindow = (iso) => {
    if (!windowFrom || !windowTo || !iso) return false;
    const t = new Date(iso).getTime();
    return t >= new Date(windowFrom).getTime() && t <= new Date(windowTo).getTime() + 86400000;
  };

  if (isLoading) return <Skeleton className="h-64 rounded-md" />;
  if (error)
    return <div className="rounded-md border border-neg/30 bg-neg/5 p-4 text-neg text-sm">Failed to load audit trail.</div>;
  if (!data?.length)
    return (
      <div data-testid="audit-empty-state" className="rounded-md border border-dashed border-border bg-card p-10 text-center">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-muted">
          <History size={22} className="text-muted-foreground" />
        </div>
        {emptyTitle && <p className="mt-3 font-heading text-lg font-bold">{emptyTitle}</p>}
        <p className="mt-2 text-sm text-muted-foreground mx-auto max-w-md">{emptyText}</p>
      </div>
    );

  return (
    <>
      <div className="rounded-md border border-border bg-card overflow-auto max-h-[38rem]">
        <Table className="tbl-sticky">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Fields Changed</TableHead>
              {onLink && <TableHead className="text-right">Explain</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((e) => {
              const hl = inWindow(e.event_timestamp_utc);
              const isMatch = matchSet.has(e.audit_event_GUID);
              const isPrimary = e.audit_event_GUID === primaryGuid;
              const isLinked = !!linkedGuid && e.audit_event_GUID === linkedGuid;
              return (
                <TableRow
                  key={e.audit_event_GUID}
                  data-testid={`audit-row-${e.audit_event_GUID}`}
                  className={`cursor-pointer hover:bg-muted/40 ${
                    isLinked
                      ? "border-l-2 border-l-pos bg-pos/[0.10] ring-1 ring-inset ring-pos/40"
                      : isMatch
                      ? "border-l-2 border-l-warn bg-warn/[0.10] ring-1 ring-inset ring-warn/40"
                      : hl
                      ? "border-l-2 border-l-warn bg-warn/[0.04]"
                      : ""
                  }`}
                  onClick={() => setSelected(e.audit_event_GUID)}
                >
                  <TableCell className="font-mono text-xs whitespace-nowrap">{fmtTime(e.event_timestamp_utc)}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5">{actorIcon(e.actor_type)} {e.actor_name_as_printed || "—"}</span>
                  </TableCell>
                  <TableCell className="text-sm">{e.subject_name_as_printed || "—"}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">{e.event_type || "Update"}</span>
                      {isMatch && (
                        <span
                          data-testid={`audit-match-${e.audit_event_GUID}`}
                          title={matchReasons[e.audit_event_GUID] || "Likely cause of this finding"}
                          className="inline-flex items-center gap-1 rounded-full bg-warn/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warn"
                        >
                          <Sparkles size={11} /> {isPrimary ? "Likely cause" : "Related"}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    {e.change_count > 0 ? num(e.change_count) : "—"}
                  </TableCell>
                  {onLink && (
                    <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
                      {isLinked ? (
                        <button
                          data-testid={`unlink-cause-${e.audit_event_GUID}`}
                          onClick={() => onLink(null)}
                          disabled={linkPending}
                          className="inline-flex items-center gap-1 rounded-full bg-pos/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-pos hover:bg-pos/25 disabled:opacity-50"
                        >
                          <Check size={12} /> Linked
                        </button>
                      ) : (
                        <button
                          data-testid={`link-cause-${e.audit_event_GUID}`}
                          onClick={() => onLink(e.audit_event_GUID)}
                          disabled={linkPending}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground disabled:opacity-50"
                        >
                          <Link2 size={12} /> This explains it
                        </button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <EventDialog eventId={selected} open={!!selected} onClose={() => setSelected(null)} />
    </>
  );
}
